// Sesión de empleado (PIN) sin cierre por inactividad.
//
// Regla del producto (2026-10-02): el empleado sigue identificado hasta
// que sale o cambia de empleado. Mientras la app está abierta la sesión
// se renueva sola en el servidor (aunque nadie toque la pantalla); al
// volver tras un rato (pantalla apagada, pestaña en segundo plano, red)
// se comprueba y se renueva en silencio. Solo se pide el PIN si el
// servidor rechaza la sesión.
//
// Si el servidor corta la sesión (empleado dado de baja, expulsado desde
// el panel), el TPV vuelve al selector de empleado.

import { openKiosk, setKioskNotice } from "src/redux/slices/employee";
import {
  clearEmployeeIdentity,
  reidentify,
  revalidateEmployeeSession,
} from "src/utils/employeeSessionRestore";

// Renovar cuando quede menos de esto (el token dura 12 h).
export const KEEP_ALIVE_REFRESH_MS = 2 * 60 * 60 * 1000;

/**
 * Qué hacer con la sesión del empleado en este momento (función pura).
 *   "none"        nada que hacer (sin empleado, sesión web personal, token holgado)
 *   "refresh"     token válido pero próximo a caducar → renovar en el servidor
 *   "reidentify"  hay empleado en pantalla pero el token ya no sirve
 */
export function decideKeepAlive({
  sessionKind,
  token,
  expiresAt,
  activeEmployee,
  now = new Date(),
}) {
  if (sessionKind === "employee-web") return "none";
  const active = Number(activeEmployee) || 0;
  if (active <= 0) return "none";
  const expiresMs = expiresAt ? new Date(expiresAt).getTime() : NaN;
  if (!token || Number.isNaN(expiresMs)) return token ? "none" : "reidentify";
  const remaining = expiresMs - now.getTime();
  if (remaining <= 0) return "reidentify";
  if (remaining < KEEP_ALIVE_REFRESH_MS) return "refresh";
  return "none";
}

let inFlight = false;

export function keepEmployeeSessionAlive() {
  return async (dispatch, getState) => {
    if (inFlight) return "busy";
    let action;
    let employeeId;
    try {
      const ls = window.localStorage;
      employeeId = Number(getState()?.employees?.currentActiveEmployee) || 0;
      action = decideKeepAlive({
        sessionKind: ls.getItem("SessionKind"),
        token: ls.getItem("EmployeeSessionToken"),
        expiresAt: ls.getItem("EmployeeSessionExpiresAt"),
        activeEmployee: employeeId,
      });
    } catch {
      return "none";
    }
    if (action === "none") return action;
    inFlight = true;
    try {
      if (action === "refresh") await dispatch(revalidateEmployeeSession(employeeId));
      else await dispatch(reidentify(employeeId));
      return action;
    } finally {
      inFlight = false;
    }
  };
}

// ── Sesión cortada por el servidor → selector de empleado ─────────────
export { EMPLOYEE_SESSION_ENDED_EVENT, notifyEmployeeSessionEnded } from "src/utils/employeeSessionEvents";

/** El TPV deja de mostrar al empleado y abre el selector (sin redirecciones). */
export function returnToEmployeeSelection(notice = null) {
  return (dispatch, getState) => {
    if (window.localStorage.getItem("SessionKind") === "employee-web") return false;
    clearEmployeeIdentity(dispatch);
    // 2026-10-06 · Con aviso (el empleado entró en otro dispositivo…) el
    // kiosko lo enseña; si ya estaba abierto, solo se pone el aviso.
    if (!getState()?.employees?.kioskOpen) dispatch(openKiosk({ manual: true, notice }));
    else if (notice) dispatch(setKioskNotice(notice));
    return true;
  };
}
